import React, { useState, useEffect, useCallback } from "react";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import Badge from "@mui/material/Badge";
import { PickersDay } from "@mui/x-date-pickers/PickersDay";
import { useDispatch, useSelector } from "react-redux";
import EventIcon from "@mui/icons-material/Event";
import { IconButton, useMediaQuery, useTheme } from "@mui/material";
import { getCalendarData } from "api/tableView";

const ServerDay = (props) => {
  const {
    highlightedDays = [],
    selectedDate,
    day,
    outsideCurrentMonth,
    ...other
  } = props;

  const formattedDay = `${day.getFullYear()}-${String(
    day.getMonth() + 1
  ).padStart(2, "0")}-${String(day.getDate()).padStart(2, "0")}`;
  const match = highlightedDays.find((item) => {
    const d = new Date(item.date);
    const itemDate = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(
      2,
      "0"
    )}-${String(d.getDate()).padStart(2, "0")}`;
    return itemDate === formattedDay;
  });

  const dotColor = match ? "success" : undefined;

  const isSelected =
    selectedDate &&
    selectedDate.getDate() === day.getDate() &&
    selectedDate.getMonth() === day.getMonth() &&
    selectedDate.getFullYear() === day.getFullYear();

  return (
    <Badge
      key={day.toString()}
      overlap="circular"
      badgeContent={match && !isSelected ? "" : undefined}
      variant={match && !isSelected ? "dot" : undefined}
      color={dotColor}>
      <PickersDay
        {...other}
        outsideCurrentMonth={outsideCurrentMonth}
        day={day}
      />
    </Badge>
  );
};

function DatePickerComp() {
  const [calendar, setCalendar] = useState(new Date());
  const [highlightedDays, setHighlightedDays] = useState([]);
  const [openDatePicker, setOpenDatePicker] = useState(false);

  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down("sm"));
  const dispatch = useDispatch();
  const selectedDate = useSelector((state) => state.customization.selectedDate);

  const fetchEvents = useCallback(async (targetDate) => {
    const source_id = localStorage.getItem("analysisStoreId");

    try {
      const response = await getCalendarData({
        year: targetDate.getFullYear(),
        month: targetDate.getMonth() + 1,
        source_id,
      });

      if (response?.data?.data) {
        setHighlightedDays(response.data.data);
      }
    } catch (error) {
      console.error("Error fetching calendar data:", error);
    }
  }, []);

  useEffect(() => {
    fetchEvents(calendar);
  }, [fetchEvents, calendar]);

  const handleMonthChange = (date) => {
    fetchEvents(date);
  };

  const handleChange = (date) => {
    setCalendar(date);
    dispatch({
      type: "selectedDate",
      payload: date,
    });
  };

  const handleCalOpen = () => {
    setOpenDatePicker(true);
    fetchEvents(calendar);
  };

  return (
    <>
      <div className={`flex items-center ${isSmallScreen ? "hidden" : ""}`}>
        <LocalizationProvider dateAdapter={AdapterDateFns}>
          <DatePicker
            className="cursor-pointer"
            sx={{
              bgcolor: "white",
              "& .MuiInputBase-root": {
                height: "40px",
              },
            }}
            slots={{
              day: ServerDay,
            }}
            slotProps={{
              textField: {
                fullWidth: true,
                bgcolor: "white",
              },
              day: {
                highlightedDays,
                selectedDate,
              },
            }}
            format="dd/MM/yyyy"
            id="date-picker-inline"
            label="Date Selected"
            value={selectedDate}
            onChange={handleChange}
            onMonthChange={handleMonthChange}
            minDate={new Date(2023, 11, 26)}
            maxDate={new Date()}
            closeOnSelect={false}
            onOpen={handleCalOpen}
            onClose={() => setOpenDatePicker(false)}
            open={openDatePicker}
          />
        </LocalizationProvider>
      </div>
      {isSmallScreen && (
        <IconButton
          sx={{ color: "black" }}
          onClick={() => setOpenDatePicker((prev) => !prev)}>
          <EventIcon />
        </IconButton>
      )}
    </>
  );
}

export default DatePickerComp;
